/**
 * The processor's load lane: the cache/inflight/event transitions a load makes,
 * and the FileEventBus arrival handlers that drive them. `createResourceProcessor.ts`
 * owns the cache, the inflight map and the public surface, and passes both by reference.
 */
import { CachedFailure } from './resourceProcessorTypes';
import { resourceFilePath } from './subResourcePath';
import * as logger from '../logger';
export function createLoadLane(ctx) {
    const { cache, inflight, eventBus, resourceType, fileEventBus, shouldProcess, process, dispose } = ctx;
    /**
     * Every in-flight resource one file's bytes can settle: the file itself plus
     * any **Sub-resource path** addressed inside it. Snapshotted before use
     * because `finishLoad` mutates `inflight`.
     */
    const awaitingFile = (filePath) => [...inflight.keys()].filter((key) => resourceFilePath(key) === filePath);
    const finishLoad = async (path, work) => {
        const startTime = performance.now();
        const flight = inflight.get(path);
        eventBus.emit(resourceType, 'loading', path);
        try {
            const result = await work();
            if (inflight.get(path) !== flight) {
                // Cleared-era completion: dispose and drop. Caching or announcing it
                // would resurrect what the clear removed. See `inflight`.
                if (result && dispose)
                    dispose(result);
                logger.info(`[${resourceType}Processor] Dropped stale load: ${path}`);
                return;
            }
            cache.set(path, result);
            inflight.delete(path);
            const elapsed = performance.now() - startTime;
            logger.info(`[${resourceType}Processor] Loaded: ${path} (${elapsed.toFixed(2)}ms)`);
            eventBus.emit(resourceType, 'loaded', path, result);
        }
        catch (error) {
            if (inflight.get(path) !== flight) {
                logger.info(`[${resourceType}Processor] Dropped stale failure: ${path}`);
                return;
            }
            const err = error instanceof Error ? error : new Error(String(error));
            cache.set(path, new CachedFailure(err)); // No retry, and a repeat request reports `err`.
            inflight.delete(path);
            const elapsed = performance.now() - startTime;
            logger.error(`[${resourceType}Processor] Failed: ${path} (${elapsed.toFixed(2)}ms)`, err);
            eventBus.emit(resourceType, 'failed', path, err);
        }
        finally {
            // The bytes are now materialised or cached as a failure, so the byte
            // bus's copy would hold a large GLB twice (a no-op in `loadDirectly` mode).
            // Only once no other address awaits the file: `clearCache` drops its in-flight
            // token too, which would strand a sibling **Sub-resource path** in `pending`.
            const file = resourceFilePath(path);
            if (awaitingFile(file).length === 0)
                fileEventBus?.clearCache(file);
        }
    };
    const fail = (path, error) => {
        inflight.delete(path);
        cache.set(path, new CachedFailure(error)); // No retry, and a repeat request reports `error`.
        eventBus.emit(resourceType, 'failed', path, error);
    };
    // Bound handler for FileEventBus events (stored once to allow proper unsubscription)
    const handleFileLoaded = async (filePath, data) => {
        // Asked about the file, never a sub-resource path: the question is what these bytes are.
        // A refusal fails only this processor's own requests, as no later arrival settles them,
        // and leaves the bytes on the bus for a sibling processor that reads them.
        if (shouldProcess && !shouldProcess(filePath, data)) {
            const refused = new Error(`${resourceType} processor does not read ${filePath}`);
            for (const path of awaitingFile(filePath))
                fail(path, refused);
            return;
        }
        // Concurrently: each address owns its flight token and cache slot, and a
        // material's `process` ends awaiting its textures, network I/O that serial
        // iteration would queue behind the previous surface's.
        await Promise.all(awaitingFile(filePath).map(async (path) => {
            if (cache.has(path)) {
                inflight.delete(path);
                return;
            }
            if (!process) {
                // File-event-bus mode requires a `process` function. Without it
                // the processor cannot materialise the resource. Treat as failure.
                fail(path, new Error(`${resourceType} processor missing process() handler`));
                return;
            }
            await finishLoad(path, () => process(path, data));
        }));
    };
    const handleFileFailed = (filePath, error) => {
        for (const path of awaitingFile(filePath))
            fail(path, error);
    };
    return { finishLoad, fail, handleFileLoaded, handleFileFailed };
}
//# sourceMappingURL=resourceProcessorLoad.js.map